import React from 'react';
import { Warning, XCircle } from '@phosphor-icons/react';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

const SummaryCards = ({ parsedData, duplicateCheck }) => (
  <div className="grid grid-cols-4 gap-3">
    <SummaryCard color="blue" value={parsedData.total_records} label="Total" />
    <SummaryCard color="emerald" value={parsedData.valid_records} label="Valid" />
    <SummaryCard color="amber" value={duplicateCheck?.duplicates_found || 0} label="Duplicates" />
    <SummaryCard color="red" value={parsedData.invalid_records} label="Errors" />
  </div>
);

const SummaryCard = ({ color, value, label }) => (
  <div className={`bg-${color}-50 rounded-lg p-3 text-center`}>
    <p className={`text-xl font-bold text-${color}-700`}>{value}</p>
    <p className={`text-xs text-${color}-600`}>{label}</p>
  </div>
);

const FieldsDetected = ({ fields }) => {
  if (!fields || fields.length === 0) return null;
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <p className="text-xs font-medium text-slate-600 mb-1">Fields detected ({fields.length}):</p>
      <div className="flex flex-wrap gap-1">
        {fields.map((f) => (
          <Badge key={f} variant="outline" className="text-xs bg-white">{f}</Badge>
        ))}
      </div>
    </div>
  );
};

const ValidationErrors = ({ errors }) => {
  if (!errors || errors.length === 0) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <XCircle size={20} className="text-red-600" />
        <p className="font-medium text-red-700">{errors.length} records have errors</p>
      </div>
      <div className="max-h-32 overflow-y-auto">
        {errors.slice(0, 5).map((err, i) => (
          <p key={`parse-err-${err.row}-${i}`} className="text-sm text-red-600">
            Row {err.row}: Missing {err.missing_fields.join(', ')}
          </p>
        ))}
      </div>
    </div>
  );
};

const DuplicatesList = ({ duplicates }) => {
  if (!duplicates || duplicates.length === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Warning size={20} className="text-amber-600" />
        <p className="font-medium text-amber-700">{duplicates.length} duplicates will be skipped</p>
      </div>
      <div className="max-h-32 overflow-y-auto">
        {duplicates.slice(0, 5).map((dup, i) => (
          <p key={`dup-${dup.row}-${i}`} className="text-sm text-amber-600">
            {dup.source === 'in_file'
              ? `Row ${dup.row}: Duplicates an earlier row in this file by ${dup.matched_field} (${dup.matched_value})`
              : `Row ${dup.row}: Matches existing by ${dup.matched_field} (${dup.matched_value})`}
          </p>
        ))}
      </div>
    </div>
  );
};

const PREVIEW_COLS_BY_TYPE = {
  patient: [
    { head: 'Medicaid ID', mono: true, getter: (r) => r.medicaid_id },
    { head: 'DOB', getter: (r) => r.date_of_birth },
    { head: 'Payer', getter: (r) => r.payer_type },
    { head: 'Gender', getter: (r) => r.gender },
  ],
  employee: [
    { head: 'Employee ID', mono: true, getter: (r) => r.employee_id },
    { head: 'Email', getter: (r) => r.email },
    { head: 'Job Title', getter: (r) => r.job_title },
    { head: 'NPI', getter: (r) => r.npi },
  ],
};

const PreviewTable = ({ records, importType }) => {
  if (!records || records.length === 0) return null;
  const cols = PREVIEW_COLS_BY_TYPE[importType] || PREVIEW_COLS_BY_TYPE.patient;
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-slate-50 px-4 py-2 border-b">
        <p className="text-sm font-medium text-slate-700">
          Preview: {records.length} records to import
        </p>
      </div>
      <div className="max-h-48 overflow-y-auto overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              {cols.map((c) => <TableHead key={c.head}>{c.head}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.slice(0, 10).map((record, i) => (
              <TableRow key={record.medicaid_id || record.employee_id || i}>
                <TableCell className="whitespace-nowrap">
                  {record.first_name} {record.middle_name ? `${record.middle_name} ` : ''}{record.last_name}
                </TableCell>
                {cols.map((c) => (
                  <TableCell key={c.head} className={c.mono ? 'font-mono text-xs' : ''}>
                    {c.getter(record)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

/**
 * Step 2 of the import flow — review parsed records, validation errors,
 * detected fields, duplicates, and preview table.
 */
const ImportReview = ({ parsedData, duplicateCheck, importType }) => (
  <div className="space-y-4">
    <SummaryCards parsedData={parsedData} duplicateCheck={duplicateCheck} />
    <FieldsDetected fields={parsedData.fields_detected} />
    <ValidationErrors errors={parsedData.errors} />
    <DuplicatesList duplicates={duplicateCheck?.duplicates} />
    <PreviewTable records={duplicateCheck?.records_to_import} importType={importType} />
  </div>
);

export default ImportReview;
